import React, { useState, useEffect } from 'react';
import './ProductionCalendar.css';

const ProductionCalendar = () => {
  const [currentMonth, setCurrentMonth] = useState(new Date());
  const [calendarData, setCalendarData] = useState({});
  const [yearStats, setYearStats] = useState({});
  const [dataSource, setDataSource] = useState({});
  const [isLoading, setIsLoading] = useState(true);
  const today = new Date();

  // Фиксированные праздничные дни России (месяц-1, день)
  const FIXED_HOLIDAYS = [
    [0, 1], [0, 2], [0, 3], [0, 4], [0, 5], [0, 6], [0, 7], [0, 8], // 1-8 января
    [1, 23], // 23 февраля
    [2, 8],  // 8 марта
    [4, 1],  // 1 мая
    [4, 9],  // 9 мая
    [5, 12], // 12 июня
    [10, 4]  // 4 ноября
  ];

  // Типичные переносы выходных (на основе исторических данных)
  // Если праздник выпадает на выходной, обычно переносится на следующий рабочий день
  const getTransferredHolidays = (year, month) => {
    const transferred = [];
    
    // Проверяем каждый фиксированный праздник в месяце
    FIXED_HOLIDAYS.forEach(([holidayMonth, holidayDay]) => {
      if (holidayMonth === month) {
        const holidayDate = new Date(year, month, holidayDay);
        const dayOfWeek = holidayDate.getDay();
        
        // Если праздник выпадает на субботу или воскресенье
        if (dayOfWeek === 0) { // Воскресенье
          // Обычно переносится на понедельник
          const nextDay = holidayDay + 1;
          if (nextDay <= new Date(year, month + 1, 0).getDate()) {
            transferred.push(nextDay);
          }
        } else if (dayOfWeek === 6) { // Суббота
          // Обычно переносится на понедельник
          const nextMonday = holidayDay + 2;
          if (nextMonday <= new Date(year, month + 1, 0).getDate()) {
            transferred.push(nextMonday);
          }
        }
      }
    });
    
    // Майские праздники часто объединяются в длинные выходные
    if (month === 4) { // Май
      // Обычно делают мост между 1 и 9 мая
      const may1 = new Date(year, 4, 1).getDay();
      const may9 = new Date(year, 4, 9).getDay();
      
      // Если между праздниками есть 1-2 рабочих дня, их часто делают выходными
      if (may1 <= 4 && may9 >= 1) {
        for (let day = 2; day <= 8; day++) {
          const date = new Date(year, 4, day);
          if (date.getDay() >= 1 && date.getDay() <= 5) {
            // Добавляем дни между праздниками как возможные выходные
            if (day === 2 || day === 3 || day === 7 || day === 8) {
              transferred.push(day);
            }
          }
        }
      }
    }
    
    // Новогодние каникулы
    if (month === 0) { // Январь
      // В России обычно каникулы с 31 декабря по 8 января
      // Иногда продлеваются до 10-11 января
      const jan9 = new Date(year, 0, 9).getDay();
      if (jan9 === 1 || jan9 === 2) { // Понедельник или вторник
        transferred.push(9);
        if (jan9 === 1) {
          transferred.push(10); // Вторник тоже может быть выходным
        }
      }
    }
    
    return transferred;
  };

  // Генерация предварительного календаря для будущих лет
  const generatePreliminaryCalendar = (year, month) => {
    const daysInMonth = new Date(year, month + 1, 0).getDate();
    const monthData = [];
    const transferredHolidays = getTransferredHolidays(year, month);
    
    for (let day = 1; day <= daysInMonth; day++) {
      const date = new Date(year, month, day);
      const dayOfWeek = date.getDay();
      
      let isHoliday = false;
      
      // Суббота или воскресенье
      if (dayOfWeek === 0 || dayOfWeek === 6) {
        isHoliday = true;
      }
      
      // Проверяем фиксированные праздники
      for (const [holidayMonth, holidayDay] of FIXED_HOLIDAYS) {
        if (month === holidayMonth && day === holidayDay) {
          isHoliday = true;
          break;
        }
      }
      
      // Проверяем перенесенные выходные
      if (transferredHolidays.includes(day)) {
        isHoliday = true;
      }
      
      // 31 декабря часто делают выходным
      if (month === 11 && day === 31) {
        isHoliday = true;
      }
      
      monthData.push(isHoliday ? 1 : 0);
    }
    
    return monthData;
  };

  const sliceYearData = (year, rawData) => {
    if (!rawData || rawData.length === 0) return null;

    const monthsInYear = [31, (new Date(year, 1, 29).getDate() === 29 ? 29 : 28), 31, 30, 31, 30, 31, 31, 30, 31, 30, 31];
    let pointer = 0;
    const result = {};

    for (let month = 0; month < 12; month++) {
      const daysInMonth = monthsInYear[month];
      const monthKey = `${year}-${month}`;
      const segment = rawData.slice(pointer, pointer + daysInMonth);

      if (segment.length === daysInMonth) {
        result[monthKey] = segment.split('').map(Number);
      } else {
        result[monthKey] = generatePreliminaryCalendar(year, month);
      }

      pointer += daysInMonth;
    }

    return result;
  };

  const fetchYearData = async (year) => {
    const response = await fetch(`https://isdayoff.ru/api/getdata?year=${year}`);

    if (response.status === 404) {
      return { status: 404, text: null };
    }

    if (!response.ok) {
      throw new Error(`HTTP error ${response.status}`);
    }

    const text = await response.text();

    // Для будущих лет провайдер иногда возвращает "нулевой" год (все рабочие дни),
    // что не является валидными официальными данными календаря.
    const isAllZeros = text && /^0+$/.test(text);
    if (!text || text.length === 0 || text.includes('error') || text.includes('Error') || isAllZeros) {
      throw new Error('Invalid calendar data');
    }

    return { status: 200, text };
  };

  // Загрузка данных календаря для месяца
  const loadCalendarData = async (year, month) => {
    const key = `${year}-${month}`;
    if (calendarData[key]) return;

    try {
      const { status, text } = await fetchYearData(year);

      if (status === 404) {
        const preliminaryData = generatePreliminaryCalendar(year, month);
        setCalendarData(prev => ({ ...prev, [key]: preliminaryData }));
        setDataSource(prev => ({ ...prev, [key]: 'preliminary' }));
        return;
      }

      const sliced = sliceYearData(year, text);
      if (sliced) {
        setCalendarData(prev => ({ ...prev, ...sliced }));
        setDataSource(prev => {
          const next = { ...prev };
          Object.keys(sliced).forEach((monthKey) => {
            next[monthKey] = 'official';
          });
          return next;
        });
      } else {
        const preliminaryData = generatePreliminaryCalendar(year, month);
        setCalendarData(prev => ({ ...prev, [key]: preliminaryData }));
        setDataSource(prev => ({ ...prev, [key]: 'preliminary' }));
      }
    } catch (error) {
      console.error(`Ошибка загрузки календаря для ${year}-${month + 1}:`, error);
      const preliminaryData = generatePreliminaryCalendar(year, month);
      setCalendarData(prev => ({ ...prev, [key]: preliminaryData }));
      setDataSource(prev => ({ ...prev, [key]: 'preliminary' }));
    }
  };

  // Загрузка статистики для года
  const loadYearStats = async (year) => {
    const yearKey = `stats-${year}`;
    if (yearStats[yearKey]) return;

    try {
      const { status, text } = await fetchYearData(year);

      if (status === 200 && text) {
        const days = text.split('').map(Number);

        if (days.length > 0 && !days.some(isNaN)) {
          const stats = {
            workDays: days.filter(d => d === 0).length,
            holidays: days.filter(d => d === 1).length,
            total: days.length,
            isPreliminary: false
          };
          setYearStats(prev => ({ ...prev, [yearKey]: stats }));
          return;
        }
      }

      if (status === 404) {
        let workDays = 0;
        let holidays = 0;

        for (let month = 0; month < 12; month++) {
          const monthData = generatePreliminaryCalendar(year, month);
          workDays += monthData.filter(d => d === 0).length;
          holidays += monthData.filter(d => d === 1).length;
        }

        const isLeapYear = (year % 4 === 0 && year % 100 !== 0) || (year % 400 === 0);
        const totalDays = isLeapYear ? 366 : 365;

        setYearStats(prev => ({
          ...prev,
          [yearKey]: {
            workDays,
            holidays,
            total: totalDays,
            isPreliminary: true
          }
        }));
        return;
      }

      throw new Error('Unexpected calendar data response');
    } catch (error) {
      console.error(`Ошибка загрузки статистики для ${year}:`, error);
      // Fallback статистика на основе типичного года
      const isLeapYear = (year % 4 === 0 && year % 100 !== 0) || (year % 400 === 0);
      setYearStats(prev => ({ 
        ...prev, 
        [yearKey]: { 
          workDays: 247, 
          holidays: isLeapYear ? 119 : 118, 
          total: isLeapYear ? 366 : 365,
          isPreliminary: true
        }
      }));
    }
  };

  // Загружаем данные для трех месяцев
  useEffect(() => {
    const loadThreeMonths = async () => {
      setIsLoading(true);
      const baseDate = new Date(currentMonth);
      
      // Загружаем текущий, предыдущий и следующий месяцы
      const loadPromises = [];
      for (let offset = -1; offset <= 1; offset++) {
        const targetDate = new Date(baseDate);
        targetDate.setMonth(baseDate.getMonth() + offset);
        const year = targetDate.getFullYear();
        const month = targetDate.getMonth();
        
        loadPromises.push(loadCalendarData(year, month));
        loadPromises.push(loadYearStats(year));
      }
      
      // Ждем загрузки всех данных
      await Promise.all(loadPromises);
      setIsLoading(false);
    };
    
    loadThreeMonths();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentMonth]);

  // Обработка клавиатуры
  useEffect(() => {
    const handleKeyPress = (e) => {
      if (e.key === 'ArrowLeft') {
        e.preventDefault();
        navigateMonth(-1);
      } else if (e.key === 'ArrowRight') {
        e.preventDefault();
        navigateMonth(1);
      }
    };

    window.addEventListener('keydown', handleKeyPress);
    return () => window.removeEventListener('keydown', handleKeyPress);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Навигация по месяцам
  const navigateMonth = (direction) => {
    setCurrentMonth(prevMonth => {
      const newDate = new Date(prevMonth);
      newDate.setMonth(prevMonth.getMonth() + direction);
      return newDate;
    });
  };

  // Обработчики навигации
  const handlePrevMonth = (e) => {
    e.preventDefault();
    e.stopPropagation();
    navigateMonth(-1);
  };

  const handleNextMonth = (e) => {
    e.preventDefault();
    e.stopPropagation();
    navigateMonth(1);
  };

  const handleYearChange = (e) => {
    const newYear = parseInt(e.target.value);
    if (!isNaN(newYear) && newYear >= 2000 && newYear <= 2100) {
      setCurrentMonth(prevMonth => {
        const newDate = new Date(prevMonth);
        newDate.setFullYear(newYear);
        return newDate;
      });
    }
  };

  // Возврат к текущему месяцу
  const handleTodayClick = () => {
    setCurrentMonth(new Date());
  };

  // Форматирование текущей даты
  const formatTodayDate = () => {
    const monthNames = [
      'января', 'февраля', 'марта', 'апреля', 'мая', 'июня',
      'июля', 'августа', 'сентября', 'октября', 'ноября', 'декабря'
    ];
    return `Сегодня: ${today.getDate()} ${monthNames[today.getMonth()]} ${today.getFullYear()}`;
  };

  // Рендер одного месяца календаря
  const renderMonth = (monthOffset = 0) => {
    const targetDate = new Date(currentMonth);
    targetDate.setMonth(currentMonth.getMonth() + monthOffset);
    
    const year = targetDate.getFullYear();
    const month = targetDate.getMonth();
    const key = `${year}-${month}`;
    const monthData = calendarData[key] || [];
    
    const firstDay = new Date(year, month, 1).getDay();
    const daysInMonth = new Date(year, month + 1, 0).getDate();
    const adjustedFirstDay = firstDay === 0 ? 6 : firstDay - 1;
    
    const days = [];
    
    // Пустые клетки в начале
    for (let i = 0; i < adjustedFirstDay; i++) {
      days.push(<div key={`empty-${i}`} className="calendar-day empty"></div>);
    }
    
    // Дни месяца
    for (let day = 1; day <= daysInMonth; day++) {
      const isHoliday = monthData[day - 1] === 1;
      const isToday = 
        year === new Date().getFullYear() &&
        month === new Date().getMonth() &&
        day === new Date().getDate();
      
      const isPreliminary = dataSource[key] === 'preliminary';
      
      days.push(
        <div
          key={day}
          className={`calendar-day ${isHoliday ? 'holiday' : 'workday'} ${isToday ? 'today' : ''} ${isPreliminary ? 'preliminary' : ''}`}
          title={isPreliminary ? 'Предварительные данные' : ''}
        >
          {day}
        </div>
      );
    }
    
    const monthNames = [
      'Январь', 'Февраль', 'Март', 'Апрель', 'Май', 'Июнь',
      'Июль', 'Август', 'Сентябрь', 'Октябрь', 'Ноябрь', 'Декабрь'
    ];
    
    const monthSource = dataSource[key];
    
    return (
      <div className="calendar-month-container">
        <div className="calendar-month-header">
          <h3>{monthNames[month]} {year}</h3>
          {monthSource === 'preliminary' && (
            <span className="data-source-badge preliminary" title="Данные еще официально не опубликованы">
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <circle cx="12" cy="12" r="10"/>
                <line x1="12" y1="8" x2="12" y2="12"/>
                <line x1="12" y1="16" x2="12.01" y2="16"/>
              </svg>
            </span>
          )}
        </div>
        <div className="calendar-weekdays compact">
          {['Пн', 'Вт', 'Ср', 'Чт', 'Пт', 'Сб', 'Вс'].map(day => (
            <div key={day} className="weekday-header">{day}</div>
          ))}
        </div>
        <div className="calendar-days compact">
          {days}
        </div>
      </div>
    );
  };

  // Статистика
  const getYearStats = () => {
    const year = currentMonth.getFullYear();
    const yearKey = `stats-${year}`;
    return yearStats[yearKey] || { workDays: 0, holidays: 0, total: 365, isPreliminary: false };
  };

  const stats = getYearStats();

  // Проверяем, есть ли предварительные данные среди отображаемых месяцев
  const hasPreliminaryData = () => {
    for (let offset = -1; offset <= 1; offset++) {
      const targetDate = new Date(currentMonth);
      targetDate.setMonth(currentMonth.getMonth() + offset);
      const year = targetDate.getFullYear();
      const month = targetDate.getMonth();
      const key = `${year}-${month}`;
      if (dataSource[key] === 'preliminary') {
        return true;
      }
    }
    return false;
  };

  return (
    <div className="production-calendar compact">
      {isLoading && (
        <div className="calendar-loading-overlay">
          <div className="calendar-loading-content">
            <div className="calendar-loading-spinner"></div>
            <p>Загрузка данных календаря...</p>
          </div>
        </div>
      )}
      <div className={`calendar-controls ${isLoading ? 'loading' : ''}`}>
        <div className="navigation-buttons">
          <button onClick={handlePrevMonth} className="nav-btn" title="Предыдущий месяц (←)">
            ←
          </button>
          <select 
            value={currentMonth.getFullYear()} 
            onChange={handleYearChange}
            className="year-select"
          >
            {[...Array(30)].map((_, i) => {
              const year = 2020 + i;
              return <option key={year} value={year}>{year}</option>;
            })}
          </select>
          <button onClick={handleNextMonth} className="nav-btn" title="Следующий месяц (→)">
            →
          </button>
        </div>

        <button 
          className="today-button"
          onClick={handleTodayClick}
          title="Перейти к текущему месяцу"
        >
          {formatTodayDate()}
        </button>
        
        <div className="calendar-stats compact">
          <span className="stat-item">
            <span className="stat-label">Рабочих:</span>
            <span className="stat-value workday">
              {stats.workDays}
              {stats.isPreliminary && '*'}
            </span>
          </span>
          <span className="stat-item">
            <span className="stat-label">Выходных:</span>
            <span className="stat-value holiday">
              {stats.holidays}
              {stats.isPreliminary && '*'}
            </span>
          </span>
          <span className="stat-item">
            <span className="stat-label">Всего:</span>
            <span className="stat-value">{stats.total}</span>
          </span>
        </div>
      </div>

      <div className={`calendar-grid three-months ${isLoading ? 'loading' : ''}`}>
        {renderMonth(-1)}
        {renderMonth(0)}
        {renderMonth(1)}
      </div>

      {hasPreliminaryData() && (
        <div className="preliminary-notice">
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
            <circle cx="12" cy="12" r="10"/>
            <line x1="12" y1="8" x2="12" y2="12"/>
            <line x1="12" y1="16" x2="12.01" y2="16"/>
          </svg>
          <span>
            Данные за отмеченные периоды еще официально не опубликованы. 
            Отображаются предварительные данные на основе стандартных праздничных дней РФ.
          </span>
        </div>
      )}

      <div className="calendar-legend compact">
        <div className="legend-item">
          <span className="legend-color workday"></span>
          <span>Рабочий день</span>
        </div>
        <div className="legend-item">
          <span className="legend-color holiday"></span>
          <span>Выходной/праздник</span>
        </div>
        <div className="legend-item">
          <span className="legend-color today"></span>
          <span>Сегодня</span>
        </div>
        <div className="legend-item">
          <span className="legend-color preliminary-legend"></span>
          <span>Предварительные данные</span>
        </div>
      </div>
    </div>
  );
};

export default ProductionCalendar;
